import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, authHeaders } from "@/lib/api";
import { AI } from "@/constants/testIds";
import { toast } from "sonner";
import { Sparkles, Copy, Loader2 } from "lucide-react";

export default function UpdateWriter() {
  const [bullets, setBullets] = useState("");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (!bullets.trim()) {
      toast.error("Add a few bullets first");
      return;
    }
    setBusy(true);
    setOut("");
    try {
      const { data } = await api.post(
        "/ai/update",
        { bullets, tone: "founder" },
        { headers: authHeaders() }
      );
      setOut(data.text || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't draft the update");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(out);
      toast.success("Copied to clipboard");
    } catch (e) {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div className="border border-white/10 bg-slate-950 p-6 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">// AI founder-update writer</div>
          <div className="font-display font-black text-xl uppercase tracking-tight mt-1">Draft the next dispatch</div>
          <p className="text-slate-400 text-sm mt-1">Rough bullets in. Polished 120–180 word founder update out.</p>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-kinetic" /> GPT-6 Luna
        </div>
      </div>

      <div>
        <Label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Your bullets</Label>
        <Textarea
          data-testid={AI.updateBullets}
          value={bullets}
          onChange={(e) => setBullets(e.target.value)}
          rows={5}
          placeholder={"- hub lease signed in riverside\n- 3 vans reserved for march pickup\n- first 12 shop partners onboarded"}
          className="rounded-none bg-slate-900 border-white/15 font-mono text-sm"
        />
      </div>

      <div className="flex gap-2">
        <Button
          data-testid={AI.updateGenerate}
          onClick={generate}
          disabled={busy}
          className="rounded-none bg-kinetic hover:bg-[#FF5E1A] text-black font-bold uppercase"
        >
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Drafting…</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate update</>}
        </Button>
        {out && (
          <Button
            data-testid={AI.updateCopy}
            onClick={copy}
            variant="outline"
            className="rounded-none border-white/15"
          >
            <Copy className="w-4 h-4 mr-2" /> Copy
          </Button>
        )}
      </div>

      {out && (
        <div
          data-testid={AI.updateOutput}
          className="bg-slate-900 border border-white/15 p-4 whitespace-pre-wrap text-sm text-slate-200 leading-relaxed"
        >
          {out}
        </div>
      )}
    </div>
  );
}
